import { useEffect, useState } from 'react';

import { styled } from '@mui/material/styles';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useFetchService } from '../../utils/fetchService';
import { PageLoader } from '../../PageLoader';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { OktaIntegrationCard } from './OktaIntegrationCard';
// eslint-disable-next-line import/extensions
import { SettingsMode } from './enum/SettingsMode.enum';
import { IntuneIntegrationCard } from './IntuneIntegrationCard';
import { Events, QuokkaEvent, useSubscribeToEvent } from '../../utils/pubSub';
import { HexnodeIntegrationCard } from './HexnodeIntegrationCard';
import { IvantiIntegrationCard } from './IvantiIntegrationCard';

export const useIdentityProviders = (accessToken: string) => {
  const { data: idpApiCredentials, error: idpError, mutate } = useFetchService('/organizations/identity-providers', accessToken);
  const isLoading = !idpApiCredentials && !idpError;
  return { idpApiCredentials, idpError, isLoading, mutate };
};

export const useIntuneMdmIntegrations = (accessToken: string) => {
  const { data: intuneSettings, error: intuneError, mutate } = useFetchService('/organizations/mdm-integrations/1', accessToken);
  const isLoadingIntuneSettings = !intuneSettings && !intuneError;
  return { intuneSettings, intuneError, isLoadingIntuneSettings, mutate };
};

export const useHexnodeMdmIntegrations = (accessToken: string) => {
  const { data: hexnodeSettings, error: hexnodeError, mutate } = useFetchService('/organizations/mdm-integrations/2', accessToken);
  const isLoadingHexnodeSettings = !hexnodeSettings && !hexnodeError;
  return { hexnodeSettings, hexnodeError, isLoadingHexnodeSettings, mutate };
};

export const useIvantiMdmIntegrations = (accessToken: string) => {
  const { data: ivantiSettings, error: ivantiError, mutate } = useFetchService('/organizations/mdm-integrations/3', accessToken);
  const isLoadingIvantiSettings = !ivantiSettings && !ivantiError;
  return { ivantiSettings, ivantiError, isLoadingIvantiSettings, mutate };
};

export const useUsersList = (accessToken: string) => {
  const { data, error, mutate } = useFetchService('/organizations/users/list', accessToken);
  const isLoading = !data && !error;

  return { data, error, isLoading, mutate };
};

export function Integrations() {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [mode, setMode] = useState({
    okta: SettingsMode.READ,
    intune: SettingsMode.READ,
    hexnode: SettingsMode.READ,
    ivanti: SettingsMode.READ,
  });
  const { idpApiCredentials = {}, idpError, isLoading, mutate: mutateOkta } = useIdentityProviders(accessToken);
  const { intuneSettings = [], intuneError, isLoadingIntuneSettings, mutate: mutateIntune } = useIntuneMdmIntegrations(accessToken);
  const { hexnodeSettings = [], hexnodeError, isLoadingHexnodeSettings, mutate: mutateHexnode } = useHexnodeMdmIntegrations(accessToken);
  const { ivantiSettings = [], ivantiError, isLoadingIvantiSettings, mutate: mutateIvanti } = useIvantiMdmIntegrations(accessToken);

  // subscribe to when INTUNE sync is done successfully so we can cause a re-render displaying the latest info
  useSubscribeToEvent(Events.INTUNE_SYNC_SUCCESSFUL);
  useSubscribeToEvent(Events.HEXNODE_SYNC_SUCCESSFUL);
  useSubscribeToEvent(Events.IVANTI_SYNC_SUCCESSFUL);

  useEffect(() => {
    QuokkaEvent.on(Events.INTUNE_SYNC_SUCCESSFUL, eventData => {
      // calling this function invalidates the cache and causes a re-render
      mutateIntune();
    });
    QuokkaEvent.on(Events.HEXNODE_SYNC_SUCCESSFUL, eventData => {
      mutateHexnode();
    });
    QuokkaEvent.on(Events.IVANTI_SYNC_SUCCESSFUL, eventData => {
      mutateIvanti();
    });
  }, []);

  const canUpdateOrg = verifyAuthPermission(AuthPermissions.UPDATE_ORG);

  if (idpError || intuneError) {
    return <div>failed to load</div>;
  }

  return (
    <>
      {isLoading || isLoadingIntuneSettings ? (
        <PageLoader />
      ) : (
        <StyledCardContainer>
          <OktaIntegrationCard
            signInUrlPlaceholder={idpApiCredentials[0]?.signInUrl ?? ''}
            managementHintPlaceholder={{
              ios: idpApiCredentials[0]?.iosManagementHint ?? '',
              android: idpApiCredentials[0]?.androidManagementHint ?? '',
            }}
            isAdmin={canUpdateOrg}
            mode={mode}
            setMode={setMode}
            mutate={mutateOkta}
          />
          <IntuneIntegrationCard
            baseUrlPlaceholder={intuneSettings?.find(setting => setting.key === 'base_url')?.value ?? 'https://graph.microsoft.com'}
            clientIdPlaceholder={intuneSettings?.find(setting => setting.key === 'client_id')?.value ?? ''}
            clientSecretPlaceholder={intuneSettings?.find(setting => setting.key === 'client_secret')?.value ?? ''}
            tenantIdPlaceholder={intuneSettings?.find(setting => setting.key === 'tenant_id')?.value ?? ''}
            lastSync={intuneSettings?.find(setting => setting.key === 'last_sync')?.value ?? ''}
            isAdmin={canUpdateOrg}
            mode={mode}
            setMode={setMode}
            mutate={mutateIntune}
          />
          <HexnodeIntegrationCard
            apiKeyPlaceholder={hexnodeSettings?.find(setting => setting.key === 'api_key')?.value ?? ''}
            portalPlaceholder={hexnodeSettings?.find(setting => setting.key === 'portal')?.value ?? ''}
            lastSync={hexnodeSettings?.find(setting => setting.key === 'last_sync')?.value ?? ''}
            isAdmin={canUpdateOrg}
            mode={mode}
            setMode={setMode}
            mutate={mutateHexnode}
          />
          <IvantiIntegrationCard
            usernamePlaceholder={ivantiSettings?.find(setting => setting.key === 'username')?.value ?? ''}
            passwordPlaceholder={ivantiSettings?.find(setting => setting.key === 'password')?.value ?? ''}
            lastSync={ivantiSettings?.find(setting => setting.key === 'last_sync')?.value ?? ''}
            isAdmin={canUpdateOrg}
            mode={mode}
            setMode={setMode}
            mutate={mutateIvanti}
          />
        </StyledCardContainer>
      )}
    </>
  );
}

const StyledCardContainer = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;
